import React, { useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { X } from 'lucide-react';
import { billingService } from '../../api/services/billing.service';
import { BusinessUsageDTO, KeyedUsage, UsageDashboardDTO, formatMoney } from '../../types/billing';
import { useAuth } from 'hooks/useAuth';
import { useTheme } from 'contexts/ThemeContext';
import { getChartTheme, seriesColor, tooltipProps } from 'styles/chartTheme';
import { formatYmd } from './closeBooksRules';
import IstDateRange from './IstDateRange';
import OrgFilterSelect from './OrgFilterSelect';
import { useLatestRequest } from './latestRequest';
import {
  fillDailySeries,
  formatCount,
  formatMinutes,
  isPlatformStaff,
  last30Days,
  shortDay,
  usageDashboardParams,
  usageKeyLabel,
  usageRangeError,
} from './billingReportRules';

const filterInput =
  'px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm';
const card = 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700';
const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';

/** Every live account is INR, and the dashboard carries no currency of its own. */
const money = (v?: number | null) => formatMoney(v ?? 0, 'INR');

const KeyedTable: React.FC<{ title: string; rows: KeyedUsage[]; label: (k: string) => string }> = ({ title, rows, label }) => (
  <div className={card}>
    <h3 className="px-4 pt-4 pb-2 text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead className="bg-gray-50 dark:bg-gray-700">
        <tr>
          <th className={`${th} text-left`}>Name</th>
          <th className={`${th} text-right`}>Charges</th>
          <th className={`${th} text-right`}>Minutes</th>
          <th className={`${th} text-right`}>Amount</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {rows.length === 0 ? (
          <tr><td colSpan={4} className="px-3 py-4 text-center text-sm text-gray-500">No usage</td></tr>
        ) : (
          rows.map((r) => (
            <tr key={r.key}>
              <td className={td}>{label(r.key)}</td>
              <td className={`${td} text-right`}>{formatCount(r.charges)}</td>
              <td className={`${td} text-right`}>{formatMinutes(r.billedMinutes)}</td>
              <td className={`${td} text-right`}>{money(r.amount)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

/**
 * What an organisation was charged over IST days: totals, per business it pays for
 * (click one to narrow everything to it), per service, per carrier and per day. Platform
 * staff pick the organisation; everyone else sees their own.
 */
const UsageDashboard: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const { resolvedTheme } = useTheme();
  const chart = getChartTheme(resolvedTheme);
  const role = (currentOrganization as any)?.role || (user as any)?.role;
  const staff = isPlatformStaff(role);

  const [orgId, setOrgId] = useState(staff ? '' : currentOrganization?.id || '');
  const [dates, setDates] = useState(last30Days());
  const [businessId, setBusinessId] = useState('');
  const [data, setData] = useState<UsageDashboardDTO | null>(null);
  const [top, setTop] = useState<BusinessUsageDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const beginRequest = useLatestRequest();

  const rangeError = usageRangeError(dates.from, dates.to);
  const needsOrg = staff && !orgId;

  useEffect(() => {
    if (rangeError || needsOrg) {
      setData(null);
      setTop([]);
      return;
    }
    const isLatest = beginRequest();
    setLoading(true);
    const q = usageDashboardParams({ organizationId: orgId, from: dates.from, to: dates.to, businessId });
    Promise.all([
      billingService.getUsageDashboard({ ...q, from: dates.from, to: dates.to }),
      billingService.getTopBusinesses({ organizationId: q.organizationId, from: dates.from, to: dates.to, limit: 10 }),
    ]).then(([d, t]) => {
      if (!isLatest()) return;
      if (d.success && d.data) {
        setData(d.data);
        setError(null);
      } else {
        setData(null);
        setError(d.error || 'Failed to load usage');
      }
      setTop(t.success && t.data ? t.data : []);
      setLoading(false);
    });
  }, [orgId, dates, businessId, rangeError, needsOrg, beginRequest]);

  const series = useMemo(
    () => (data ? fillDailySeries(data.daily, data.from, data.to) : []),
    [data],
  );
  const businessName = (id: string) =>
    data?.byBusiness.find((b) => b.businessId === id)?.businessName
    || top.find((b) => b.businessId === id)?.businessName
    || id;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Usage</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Charged usage from the ledger over IST days, both inclusive (at most 400).
          {data?.organizationName ? ` ${data.organizationName}.` : ''}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {staff && (
          <OrgFilterSelect
            value={orgId}
            allLabel="Choose an organization"
            required
            onChange={(v) => {
              setOrgId(v);
              setBusinessId('');
            }}
          />
        )}
        <IstDateRange label="Usage" from={dates.from} to={dates.to} inputClassName={filterInput} onChange={setDates} />
        <button
          type="button"
          onClick={() => setDates(last30Days())}
          className="self-end px-3 py-2 text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          Last 30 days
        </button>
        {businessId && (
          <span className="self-end inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
            Business: {businessName(businessId)}
            <button type="button" onClick={() => setBusinessId('')} aria-label="Clear business filter">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      </div>

      {needsOrg ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Choose an organization to see its usage.</p>
      ) : rangeError ? (
        <p className="text-sm text-red-600">{rangeError}</p>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : !data ? (
        loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
          </div>
        ) : null
      ) : (
        <div className={`space-y-6 ${loading ? 'opacity-60' : ''}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`${card} p-4`}>
              <p className="text-sm text-gray-500 dark:text-gray-400">Amount</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{money(data.totals.amount)}</p>
            </div>
            <div className={`${card} p-4`}>
              <p className="text-sm text-gray-500 dark:text-gray-400">Charges</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{formatCount(data.totals.charges)}</p>
            </div>
            <div className={`${card} p-4`}>
              <p className="text-sm text-gray-500 dark:text-gray-400">Billed minutes</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{formatMinutes(data.totals.billedMinutes)}</p>
            </div>
          </div>

          <div className={`${card} p-4`}>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              Amount per day · {formatYmd(data.from)} – {formatYmd(data.to)}
            </h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={series}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                <XAxis dataKey="day" tickFormatter={shortDay} stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} />
                <YAxis stroke={chart.axis} tick={{ fill: chart.axis, fontSize: 12 }} />
                <Tooltip
                  {...tooltipProps(resolvedTheme)}
                  labelFormatter={(d: any) => formatYmd(String(d))}
                  formatter={(v: any) => [money(Number(v)), 'Amount']}
                />
                <Bar dataKey="amount" name="Amount" fill={seriesColor(resolvedTheme, 1)} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className={`${card} lg:col-span-2 overflow-x-auto`}>
              <h3 className="px-4 pt-4 pb-2 text-sm font-semibold text-gray-900 dark:text-white">
                By business <span className="font-normal text-gray-500 dark:text-gray-400">· click one to filter</span>
              </h3>
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className={`${th} text-left`}>Business</th>
                    <th className={`${th} text-right`}>Charges</th>
                    <th className={`${th} text-right`}>Minutes</th>
                    <th className={`${th} text-right`}>Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {data.byBusiness.length === 0 ? (
                    <tr><td colSpan={4} className="px-3 py-4 text-center text-sm text-gray-500">No usage</td></tr>
                  ) : (
                    data.byBusiness.map((b) => (
                      <tr
                        key={b.businessId}
                        onClick={() => setBusinessId(b.businessId === businessId ? '' : b.businessId)}
                        className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 ${b.businessId === businessId ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`}
                      >
                        <td className={td}>
                          {b.businessName || b.businessId}
                          {b.direct && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(own usage)</span>}
                        </td>
                        <td className={`${td} text-right`}>{formatCount(b.charges)}</td>
                        <td className={`${td} text-right`}>{formatMinutes(b.billedMinutes)}</td>
                        <td className={`${td} text-right`}>{money(b.amount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className={card}>
              <h3 className="px-4 pt-4 pb-2 text-sm font-semibold text-gray-900 dark:text-white">Top businesses</h3>
              <ol className="divide-y divide-gray-100 dark:divide-gray-700">
                {top.length === 0 ? (
                  <li className="px-4 py-4 text-sm text-gray-500">No usage</li>
                ) : (
                  top.map((b, i) => (
                    <li key={b.businessId} className="px-4 py-2 flex items-center justify-between gap-3 text-sm">
                      <button
                        type="button"
                        onClick={() => setBusinessId(b.businessId)}
                        className="text-left text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 truncate"
                      >
                        {i + 1}. {b.businessName || b.businessId}
                      </button>
                      <span className="text-gray-900 dark:text-gray-100 whitespace-nowrap">{money(b.amount)}</span>
                    </li>
                  ))
                )}
              </ol>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <KeyedTable title="By service" rows={data.byServiceType} label={usageKeyLabel} />
            {/* Empty for a business: the server withholds carrier names from it. */}
            {data.byCarrier.length > 0 && (
              <KeyedTable title="By carrier" rows={data.byCarrier} label={(k) => k || 'Unknown'} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageDashboard;
